/**
 * Role-based access control utilities
 * Manages user roles and permissions
 */

// User roles
export const USER_ROLES = {
    FREE: 'Free',
    HOST: 'Host',
};

/**
 * Check if user is a Host (subscribed user)
 * @param {Object} user - User object from UserContext
 * @returns {boolean}
 */
export const isHostUser = (user) => {
    return user?.role === USER_ROLES.HOST;
};

/**
 * Check if user is Free (non-subscribed user)
 * @param {Object} user - User object from UserContext
 * @returns {boolean}
 */
export const isFreeUser = (user) => {
    return user?.role === USER_ROLES.FREE;
};

/**
 * Check if user has access to a feature
 * @param {Object} user - User object from UserContext
 * @param {string} feature - Feature name to check
 * @returns {boolean}
 */
export const hasFeatureAccess = (user, feature) => {
    const hostFeatures = [
        'create_community',
        'host_community',
        'no_ads',
        'no_service_fee',
        'enhanced_uploads',
        'stories',
        'blue_tick',
        'orange_glow',
        'sell_products',
    ];

    // Host users have access to all features
    if (isHostUser(user)) {
        return true;
    }

    // Free users don't have access to host-only features
    if (hostFeatures.includes(feature)) {
        return false;
    }

    // Default: allow access
    return true;
};

/**
 * Get user role display name
 * @param {Object} user - User object from UserContext
 * @returns {string}
 */
export const getUserRoleName = (user) => {
    return isHostUser(user) ? 'Host' : 'Free';
};

/**
 * Check if user can create a community
 * @param {Object} user - User object from UserContext
 * @returns {boolean}
 */
export const canCreateCommunity = (user) => {
    return isHostUser(user);
};

/**
 * Check if user can message a Host user
 * Free users may be restricted from messaging Host users
 * @param {Object} currentUser - Current user object
 * @param {Object} targetUser - Target user to message
 * @returns {boolean}
 */
export const canMessageUser = (currentUser, targetUser) => {
    // Host users can message anyone
    if (isHostUser(currentUser)) {
        return true;
    }

    // Free users can message other Free users
    if (isFreeUser(targetUser)) {
        return true;
    }

    // This is where you'd add logic for Free -> Host messaging restrictions
    // For now, return true (allow all messages)
    return true;
};

/**
 * Get subscription status badge color
 * @param {string} status - Subscription status
 * @returns {string} - Tailwind color classes
 */
export const getSubscriptionBadgeColor = (status) => {
    switch (status) {
        case 'active':
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
        case 'trial':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
        case 'past_due':
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        case 'canceled':
        case 'canceling':
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
};

/**
 * Format subscription status for display
 * @param {string} status - Subscription status
 * @returns {string}
 */
export const formatSubscriptionStatus = (status) => {
    if (!status) return 'No subscription';

    const statusMap = {
        active: 'Active',
        trial: 'Trial',
        past_due: 'Past Due',
        canceled: 'Canceled',
        canceling: 'Canceling',
        none: 'No subscription',
    };

    return statusMap[status] || status;
};

/**
 * Check if user can sell products
 * Only Host users can sell products in the store
 * @param {Object} user - User object from UserContext
 * @returns {boolean}
 */
export const canSellProducts = (user) => {
    return isHostUser(user);
};

/**
 * Calculate the service fee for a purchase
 * Free users pay 5% fee, Host users pay no fee
 * @param {Object} user - User object from UserContext
 * @param {number} price - Product price in dollars
 * @returns {Object} - { fee, total }
 */
export const calculatePurchaseFee = (user, price) => {
    const SERVICE_FEE_PERCENTAGE = 0.05; // 5%

    if (isHostUser(user)) {
        return {
            fee: 0,
            total: price,
        };
    }

    // Free users pay 5% fee
    const fee = price * SERVICE_FEE_PERCENTAGE;
    const total = price + fee;

    return {
        fee: parseFloat(fee.toFixed(2)),
        total: parseFloat(total.toFixed(2)),
    };
};

